import { Rect as GRect, Text as GText } from '@antv/g';
import {
    Badge,
    CommonEvent,
    ExtensionCategory,
    Graph,
    GraphEvent,
    iconfont,
    Label,
    Rect,
    register,
    treeToGraphData,
} from '@antv/g6';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SubFilterDropdown from 'components/molecules/GrowthAnalysis/SubFilterDropdown';
import SubFilterCompareIcon from 'components/molecules/GrowthAnalysis/SubFilterCompareIcon';
import { cloneDeep, findLastIndex, isEqual, uniqBy } from 'lodash';
import { countNodes } from './data/hierarchicalTreeData';
import { formatWord, removeFunctionFromProps } from 'utils/helperMethods';

const nullAttrTypeOption = { label: null, value: null, dimension: null };

const MAX_HIERARCHY_ITEMS = 5;

export interface SubFilterItem {
    label: string | null;
    value: number | null;
    dimension: string | null;
    isAttribute?: boolean;
    key?: string;
}

const COLORS = {
    B: '#1783FF',
    R: '#F46649',
    Y: '#DB9D0D',
    G: '#60C42D',
    DI: '#A7A7A7',
    PNP: '#5F49BC',
};
const GREY_COLOR = '#CED4D9';

class TreeNode extends Rect {
    get data() {
        return this.context.model.getNodeLikeDatum(this.id);
    }

    get childrenData() {
        return this.context.model.getChildrenData(this.id);
    }

    getLabelStyle(attributes: any) {
        const [width, height] = this.getSize(attributes);
        return {
            x: -width / 2 + 8,
            y: -height / 2 + 16,
            text: String(this.data.name || ''),
            fontSize: 12,
            opacity: 0.85,
            fill: '#000',
        };
    }

    getAttributeTypeStyle(attributes: any) {
        const [width, height] = this.getSize(attributes);
        const attributeType = this.data.attributeType;
        return {
            x: -width / 2 + 8,
            y: -height / 2 + 38,
            text: attributeType && typeof attributeType === 'string' ? formatWord(attributeType) : '',
            fontSize: 10,
            opacity: 0.5,
            fill: '#666',
        };
    }

    drawAttributeTypeShape(attributes: any, container: any) {
        if (this.data.attributeType) {
            const attributeTypeStyle = this.getAttributeTypeStyle(attributes);
            this.upsert('attributeType', GText, attributeTypeStyle, container);
        }
    }

    getPriceStyle(attributes: any) {
        const [width, height] = this.getSize(attributes);
        return {
            x: -width / 2 + 8,
            y: height / 2 - 8,
            text: String(this.data.label || ''),
            fontSize: 16,
            fill: '#000',
            opacity: 0.85,
        };
    }

    drawPriceShape(attributes: any, container: any) {
        const priceStyle = this.getPriceStyle(attributes);
        this.upsert('price', GText, priceStyle, container);
    }

    getCurrencyStyle(attributes: any) {
        const [, height] = this.getSize(attributes);
        return {
            x: this.shapeMap['price'].getLocalBounds().max[0] + 4,
            y: height / 2 - 8,
            text: String(this.data.currency || ''),
            fontSize: 12,
            fill: '#000',
            opacity: 0.75,
        };
    }

    drawCurrencyShape(attributes: any, container: any) {
        const currencyStyle = this.getCurrencyStyle(attributes);
        this.upsert('currency', GText, currencyStyle, container);
    }

    getPercentStyle(attributes: any) {
        const [width, height] = this.getSize(attributes);
        return {
            x: width / 2 - 4,
            y: height / 2 - 8,
            text: `${((Number(this.data.variableValue) || 0) * 100).toFixed(2)}%`,
            fontSize: 12,
            textAlign: 'right' as const,
            fill: COLORS[this.data.status as keyof typeof COLORS] || COLORS.DI,
        };
    }

    drawPercentShape(attributes: any, container: any) {
        const percentStyle = this.getPercentStyle(attributes);
        this.upsert('percent', GText, percentStyle, container);
    }

    getTriangleStyle(attributes: any) {
        const percentMinX = this.shapeMap['percent'].getLocalBounds().min[0];
        const [, height] = this.getSize(attributes);
        return {
            fill: COLORS[this.data.status as keyof typeof COLORS] || COLORS.DI,
            x: this.data.variableUp ? percentMinX - 18 : percentMinX,
            y: height / 2 - 16,
            fontFamily: 'iconfont',
            fontSize: 16,
            text: '\ue62d',
            transform: this.data.variableUp ? [] : [['rotate', 180]],
        };
    }

    drawTriangleShape(attributes: any, container: any) {
        const triangleStyle = this.getTriangleStyle(attributes);
        this.upsert('triangle', Label, triangleStyle as any, container);
    }

    getVariableStyle(attributes: any) {
        const [, height] = this.getSize(attributes);
        return {
            fill: '#000',
            fontSize: 12,
            opacity: 0.45,
            text: String(this.data.variableName || ''),
            textAlign: 'right' as const,
            x: this.shapeMap['triangle'].getLocalBounds().min[0] - 4,
            y: height / 2 - 8,
        };
    }

    drawVariableShape(attributes: any, container: any) {
        const variableStyle = this.getVariableStyle(attributes);
        this.upsert('variable', GText, variableStyle, container);
    }

    getCollapseStyle(attributes: any) {
        if (this.childrenData.length === 0) return false;
        const { collapsed } = attributes;
        const [width] = this.getSize(attributes);
        return {
            backgroundFill: '#fff',
            backgroundHeight: 16,
            backgroundLineWidth: 1,
            backgroundRadius: 0,
            backgroundStroke: GREY_COLOR,
            backgroundWidth: 16,
            cursor: 'pointer' as const,
            fill: GREY_COLOR,
            fontSize: 16,
            text: collapsed ? '+' : '-',
            textAlign: 'center' as const,
            textBaseline: 'middle' as const,
            x: width / 2,
            y: 0,
        };
    }

    drawCollapseShape(attributes: any, container: any) {
        const collapseStyle = this.getCollapseStyle(attributes);
        const btn = this.upsert('collapse', Badge, collapseStyle, container);

        if (btn && !Reflect.has(btn, '__bind__')) {
            Reflect.set(btn, '__bind__', true);
            btn.addEventListener(CommonEvent.CLICK, (e: any) => {
                // Stop propagation to prevent node:click from firing
                e.stopPropagation();
                const { collapsed } = this.attributes;
                const graph = this.context.graph;
                if (collapsed) graph.expandElement(this.id);
                else graph.collapseElement(this.id);
            });
        }
    }

    getProcessBarStyle(attributes: any) {
        const { rate, status } = this.data;
        const { radius } = attributes;
        const color = COLORS[status as keyof typeof COLORS] || COLORS.DI;
        const percent = `${Number(rate) * 100}%`;
        const [width, height] = this.getSize(attributes);
        return {
            x: -width / 2,
            y: height / 2 - 4,
            width: width,
            height: 4,
            radius: [0, 0, radius, radius],
            fill: `linear-gradient(to right, ${color} ${percent}, ${GREY_COLOR} ${percent})`,
        };
    }

    drawProcessBarShape(attributes: any, container: any) {
        const processBarStyle = this.getProcessBarStyle(attributes);
        this.upsert('process-bar', GRect, processBarStyle, container);
    }

    getKeyStyle(attributes: any) {
        const keyStyle = super.getKeyStyle(attributes);
        return {
            ...keyStyle,
            fill: '#fff',
            lineWidth: 1,
            stroke: GREY_COLOR,
        };
    }

    render(attributes = this.parsedAttributes, container: any) {
        super.render(attributes, container);

        this.drawAttributeTypeShape(attributes, container);
        this.drawPriceShape(attributes, container);
        this.drawCurrencyShape(attributes, container);
        this.drawPercentShape(attributes, container);
        this.drawTriangleShape(attributes, container);
        this.drawVariableShape(attributes, container);
        this.drawProcessBarShape(attributes, container);
        this.drawCollapseShape(attributes, container);
    }
}

interface HierarchicalTreeChartProps {
    containerId?: string;
    data: any;
    height?: number;
    width: number;
    onNodeClick?: (nodeData: any) => void;
    loading?: boolean;
}

const HierarchicalTreeChart: React.FC<HierarchicalTreeChartProps> = ({
    containerId = 'hierarchical-tree-container',
    data,
    height = 600,
    width,
    onNodeClick,
    loading = false,
}) => {
    const graphRef = useRef<Graph | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const styleRef = useRef<HTMLStyleElement | null>(null);
    // Store onNodeClick in a ref to avoid re-creating the graph when it changes
    const onNodeClickRef = useRef(onNodeClick);

    // Ref to track previous subfilter items for detecting changes
    const previousSubFilterItems = useRef<SubFilterItem[]>([]);
    // Ref to track the previous node ID to detect when a new bar is selected
    const previousNodeId = useRef<string | null>(null);

    // Reset initialization when data changes (new bar selection)
    // This ensures that when user selects a new bar, we re-initialize the subfilter state
    useEffect(() => {
        const currentNodeId = data?.id || null;

        if (!data) {
            // No data - reset everything
            previousSubFilterItems.current = [];
            previousNodeId.current = null;
        } else if (currentNodeId && currentNodeId !== previousNodeId.current) {
            // New bar selected (different node ID) - reset initialization state to allow re-initialization
            previousSubFilterItems.current = [];
            previousNodeId.current = currentNodeId;
        }
    }, [data]);

    // Keep the ref updated with the latest callback
    useEffect(() => {
        onNodeClickRef.current = onNodeClick;
    }, [onNodeClick]);

    // Effect for initial graph setup (only runs once or when container/size changes)
    useEffect(() => {
        // Add iconfont style
        if (!styleRef.current) {
            const style = document.createElement('style');
            style.innerHTML = `@import url('${iconfont.css}');`;
            document.head.appendChild(style);
            styleRef.current = style;
        }

        // Register custom node (only once)
        register(ExtensionCategory.NODE, 'tree-node', TreeNode);

        // Cleanup
        return () => {
            if (graphRef.current) {
                graphRef.current.destroy();
                graphRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // Skip runs with no data or an unmeasured (0) width to avoid transient
        // re-runs and rendering into a zero-size container.
        if (!containerRef.current || !data || !width) return;

        let cancelled = false;

        const initOrUpdateGraph = async () => {
            try {
                const graphData = treeToGraphData(data, {
                    getNodeData: (datum: any, depth: number) => {
                        if (!datum.style) datum.style = {};
                        // Always keep nodes with children expanded, collapse only leaf nodes
                        const hasChildren = datum.children && datum.children.length > 0;
                        datum.style.collapsed = !hasChildren;
                        if (!hasChildren) return datum;
                        const { children, ...restDatum } = datum;
                        return { ...restDatum, children: children.map((child: any) => child.id) };
                    },
                });

                const noOfNodes = countNodes(data);

                // Create new graph instance
                const graph = new Graph({
                    container: containerRef.current!,
                    autoFit: {
                        type: 'view', // Adaptation type: 'view' or 'center'
                        options: {
                            // Only applicable to 'view' type
                            when: noOfNodes < 5 ? 'overflow' : 'always', // When to adapt: 'overflow' (only when content overflows) or 'always' (always adapt)
                            direction: 'both', // Adaptation direction: 'x', 'y', or 'both'
                        },
                        animation: {
                            // Adaptation animation effect
                            duration: 1000, // Animation duration (milliseconds)
                            easing: 'ease-in-out', // Animation easing function
                        },
                    },
                    data: graphData,
                    node: {
                        type: 'tree-node',
                        style: {
                            size: [202, 75],
                            ports: [{ placement: 'left' }, { placement: 'right' }],
                            radius: 4,
                        },
                    },
                    edge: {
                        type: 'cubic-horizontal',
                        style: {
                            stroke: GREY_COLOR,
                            // Ensure edges connect from right port of parent to left port of child
                            sourcePort: 'right',
                            targetPort: 'left',
                        },
                    },
                    layout: {
                        type: 'indented',
                        direction: 'LR',
                        dropCap: false,
                        indent: 300,
                        getHeight: () => 75,
                        preLayout: false,
                    },
                    zoomRange: [0.1, 100],
                    behaviors: ['zoom-canvas', 'drag-canvas'],
                    ...(width && { width }),
                    ...(height && { height }),
                });

                // render() performs layout internally in g6 v5; await it so we never
                // tear the graph down mid-layout.
                await graph.render();

                if (cancelled) {
                    // A newer effect run superseded this one; discard our graph.
                    graph.destroy();
                    return;
                }

                // Safe to swap: the previous graph already finished rendering.
                if (graphRef.current) graphRef.current.destroy();
                graphRef.current = graph;
            } catch (error) {
                console.error('Error loading graph:', error);
            }
        };

        initOrUpdateGraph();

        return () => {
            cancelled = true;
        };
    }, [containerId, data, height, width]);

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            {/* Graph Container */}
            <div style={{ position: 'relative', width: '100%', height: height || 600 }}>
                <div id={containerId} ref={containerRef} style={{ width: '100%', height: '100%' }} />
                {loading && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 10,
                        }}
                    >
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                    </div>
                )}
            </div>
        </div>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    const [clonePrev, cloneNext] = removeFunctionFromProps({ ...prevProps }, { ...nextProps }, ['tooltipConfig']); //remove function type from props for referential equality
    return isEqual(clonePrev, cloneNext);
}

export default React.memo(HierarchicalTreeChart, arePropsEqual);
